import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { ShieldCheck, Plus, Edit, Trash2, Package, AlertCircle, Search } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Allowed fields kept in sync with the backend `ALLOWED_RULE_FIELDS`.
const FIELD_OPTIONS = [
  { key: 'country',         label: 'الدولة' },
  { key: 'customer_name',   label: 'اسم العميل' },
  { key: 'customer_phone',  label: 'رقم التواصل' },
  { key: 'activity_type',   label: 'نوع النشاط' },
];

const blankForm = { label: '', master_ids: [], required_fields: [], active: true };

export default function SuperFieldRules() {
  const [rules, setRules] = useState([]);
  const [products, setProducts] = useState([]); // distinct masters
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([
        axios.get(`${API_URL}/api/super/field-rules`),
        axios.get(`${API_URL}/api/super/products`),
      ]);
      setRules(r.data || []);
      // Reduce products to distinct masters
      const seen = new Set();
      const uniq = [];
      for (const prod of (p.data || [])) {
        const m = prod.master_id || prod.id;
        if (seen.has(m)) continue;
        seen.add(m);
        uniq.push({ master_id: m, name: prod.name, hidden: !!prod.hidden });
      }
      uniq.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ar'));
      setProducts(uniq);
    } catch (e) {
      toast.error('فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const productNameByMasterId = useMemo(() => {
    const m = {};
    for (const p of products) m[p.master_id] = p.name;
    return m;
  }, [products]);

  const openAdd = () => {
    setEditing(null);
    setForm(blankForm);
    setProductSearch('');
    setOpen(true);
  };

  const openEdit = (rule) => {
    setEditing(rule.id);
    setForm({
      label: rule.label || '',
      master_ids: [...(rule.master_ids || [])],
      required_fields: [...(rule.required_fields || [])],
      active: !!rule.active,
    });
    setProductSearch('');
    setOpen(true);
  };

  const toggleField = (key) => {
    setForm((f) => ({
      ...f,
      required_fields: f.required_fields.includes(key)
        ? f.required_fields.filter((x) => x !== key)
        : [...f.required_fields, key],
    }));
  };

  const toggleMaster = (mid) => {
    setForm((f) => ({
      ...f,
      master_ids: f.master_ids.includes(mid)
        ? f.master_ids.filter((x) => x !== mid)
        : [...f.master_ids, mid],
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) {
      toast.error('اسم القاعدة مطلوب');
      return;
    }
    if (form.master_ids.length === 0) {
      toast.error('اختر منتجاً واحداً على الأقل');
      return;
    }
    if (form.required_fields.length === 0) {
      toast.error('اختر حقلاً إلزامياً واحداً على الأقل');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/super/field-rules/${editing}`, form);
        toast.success('تم تحديث القاعدة');
      } else {
        await axios.post(`${API_URL}/api/super/field-rules`, form);
        toast.success('تم إنشاء القاعدة');
      }
      setOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل الحفظ');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`حذف قاعدة «${rule.label}»؟`)) return;
    try {
      await axios.delete(`${API_URL}/api/super/field-rules/${rule.id}`);
      toast.success('تم حذف القاعدة');
      fetchAll();
    } catch (err) {
      toast.error('فشل الحذف');
    }
  };

  const handleToggleActive = async (rule) => {
    try {
      await axios.put(`${API_URL}/api/super/field-rules/${rule.id}`, { active: !rule.active });
      fetchAll();
    } catch (err) {
      toast.error('فشل التحديث');
    }
  };

  const fieldLabels = (keys) =>
    (keys || [])
      .map((k) => FIELD_OPTIONS.find((f) => f.key === k)?.label || k)
      .join('، ');

  const filteredProducts = useMemo(() => {
    const q = productSearch.trim();
    if (!q) return products;
    return products.filter((p) => (p.name || '').includes(q));
  }, [products, productSearch]);

  return (
    <div className="space-y-6" dir="rtl" data-testid="field-rules-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-brand_blue/10 flex items-center justify-center">
            <ShieldCheck className="h-6 w-6 text-brand_blue" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-foreground">قواعد الحقول الإلزامية</h1>
            <p className="text-sm text-foreground/60">
              اختر المنتجات التي تريد جعل حقول معينة إلزامية عليها عند تسجيل البيع.
            </p>
          </div>
        </div>
        <Button onClick={openAdd} className="gap-2" data-testid="add-rule-button">
          <Plus className="h-4 w-4" />
          إضافة قاعدة
        </Button>
      </div>

      <Card className="p-4 bg-brand_blue/5 border-brand_blue/20 flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-brand_blue mt-0.5 flex-shrink-0" />
        <div className="text-sm text-foreground/80 leading-relaxed">
          <p>
            <span className="font-bold">كيف تعمل القواعد؟</span>{' '}
            تختار منتجاً (أو أكثر) وتُحدد الحقول التي يجب على الموظف ملؤها عند تسجيل بيع لهذا
            المنتج. إذا تُركت فارغة — يُمنع الحفظ ويظهر إشعار بالحقول الناقصة.
          </p>
          <p className="mt-1">
            مثال: قاعدة باسم «الشحن الدولي» مرتبطة بالمنتجات «شحن دولي بسيط» و«شحن دولي مميز»
            تجعل الدولة إلزامية لهذه المنتجات فقط، وليس لأي منتج يحوي كلمة «دولي».
          </p>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="rules-table">
            <thead className="bg-accent/40 border-b border-border">
              <tr>
                <th className="text-right py-3 px-4 text-sm font-bold">اسم القاعدة</th>
                <th className="text-right py-3 px-4 text-sm font-bold">المنتجات المرتبطة</th>
                <th className="text-right py-3 px-4 text-sm font-bold">الحقول الإلزامية</th>
                <th className="text-right py-3 px-4 text-sm font-bold">الحالة</th>
                <th className="text-right py-3 px-4 text-sm font-bold">إجراء</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="py-12 text-center text-foreground/50">
                    جاري التحميل...
                  </td>
                </tr>
              ) : rules.length === 0 ? (
                <tr>
                  <td colSpan="5" className="py-12 text-center text-foreground/50">
                    لا توجد قواعد بعد — اضغط «إضافة قاعدة» للبدء
                  </td>
                </tr>
              ) : (
                rules.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-border hover:bg-accent/30"
                    data-testid={`rule-row-${r.id}`}
                  >
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-100 text-amber-800 font-bold text-sm">
                        <ShieldCheck className="h-3.5 w-3.5" />
                        {r.label}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap gap-1 max-w-md">
                        {(r.master_ids || []).length === 0 ? (
                          <span className="text-foreground/30 text-xs">لا يوجد</span>
                        ) : (
                          (r.master_ids || []).map((mid) => (
                            <span
                              key={mid}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-brand_blue/10 text-brand_blue text-xs font-bold"
                            >
                              <Package className="h-3 w-3" />
                              {productNameByMasterId[mid] || '— منتج محذوف —'}
                            </span>
                          ))
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap gap-1">
                        {(r.required_fields || []).map((f) => (
                          <span
                            key={f}
                            className="px-2 py-0.5 rounded bg-destructive/10 text-destructive text-xs font-bold"
                          >
                            {FIELD_OPTIONS.find((o) => o.key === f)?.label || f}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <Switch
                        checked={r.active}
                        onCheckedChange={() => handleToggleActive(r)}
                        data-testid={`rule-active-${r.id}`}
                      />
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(r)}
                          className="h-8 w-8 p-0"
                          data-testid={`edit-rule-${r.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(r)}
                          className="h-8 w-8 p-0 text-destructive"
                          data-testid={`delete-rule-${r.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add / Edit Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="rule-dialog">
          <DialogHeader>
            <DialogTitle className="text-right">
              {editing ? 'تعديل القاعدة' : 'إضافة قاعدة جديدة'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="rule-label">
                اسم القاعدة <span className="text-destructive">*</span>
              </Label>
              <Input
                id="rule-label"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="مثال: الشحن الدولي"
                data-testid="rule-label-input"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>
                المنتجات المرتبطة <span className="text-destructive">*</span>
                <span className="text-xs font-normal text-foreground/55 mr-2">
                  ({form.master_ids.length} محدد)
                </span>
              </Label>

              <div className="relative">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40" />
                <Input
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  placeholder="ابحث عن منتج..."
                  className="pr-9"
                  data-testid="rule-product-search"
                />
              </div>

              <div className="max-h-48 overflow-y-auto border border-border rounded-md divide-y divide-border">
                {filteredProducts.length === 0 ? (
                  <div className="p-3 text-center text-sm text-foreground/50">
                    {products.length === 0 ? 'لا توجد منتجات' : 'لا نتائج للبحث'}
                  </div>
                ) : (
                  filteredProducts.map((p) => {
                    const checked = form.master_ids.includes(p.master_id);
                    return (
                      <label
                        key={p.master_id}
                        className={`flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors ${
                          checked ? 'bg-brand_blue/10' : 'hover:bg-accent/30'
                        }`}
                        data-testid={`rule-product-${p.master_id}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleMaster(p.master_id)}
                          className="accent-brand_blue"
                        />
                        <Package className="h-3.5 w-3.5 text-foreground/50" />
                        <span className={`text-sm flex-1 ${checked ? 'font-bold text-brand_blue' : ''}`}>
                          {p.name}
                        </span>
                        {p.hidden && (
                          <span className="text-[10px] text-foreground/50">(مخفي)</span>
                        )}
                      </label>
                    );
                  })
                )}
              </div>
              {form.master_ids.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-1">
                  {form.master_ids.map((mid) => (
                    <span
                      key={mid}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-brand_blue/10 text-brand_blue text-xs font-bold"
                    >
                      {productNameByMasterId[mid] || '—'}
                      <button
                        type="button"
                        onClick={() => toggleMaster(mid)}
                        className="text-brand_blue/70 hover:text-destructive"
                        aria-label="إزالة"
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>
                الحقول الإلزامية <span className="text-destructive">*</span>
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {FIELD_OPTIONS.map((opt) => {
                  const checked = form.required_fields.includes(opt.key);
                  return (
                    <label
                      key={opt.key}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer border transition-colors ${
                        checked
                          ? 'bg-destructive/10 border-destructive text-destructive font-bold'
                          : 'bg-white border-border hover:bg-accent/30'
                      }`}
                      data-testid={`rule-field-${opt.key}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleField(opt.key)}
                        className="accent-destructive"
                      />
                      <span className="text-sm">{opt.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-border">
              <Label htmlFor="rule-active" className="cursor-pointer">القاعدة مفعّلة</Label>
              <Switch
                id="rule-active"
                checked={form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })}
                data-testid="rule-active-switch"
              />
            </div>

            {form.required_fields.length > 0 && form.master_ids.length > 0 && form.label && (
              <div className="text-xs p-2.5 rounded-md bg-amber-50 border border-amber-200 text-amber-800">
                <span className="font-bold">معاينة:</span> قاعدة «{form.label}» ستجعل{' '}
                <span className="font-bold">{fieldLabels(form.required_fields)}</span>{' '}
                إلزامية على <span className="font-bold">{form.master_ids.length} منتج</span>{' '}
                عند تسجيل البيع.
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                إلغاء
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-rule-button">
                {saving ? 'جاري الحفظ...' : editing ? 'حفظ التعديلات' : 'إنشاء القاعدة'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
